import { useCallback, useEffect, useState } from 'react'
import {
  Bidi, Button, Card, CardHeader, Field, FieldInput, LoadingState,
} from '@hk/ui'
import {
  addFaultCategory, codeWellFormed, deleteFaultCategory, fetchFaultCategoryAdmin,
  formatCount, saveFaultCategory, setFaultCategoryActive, suggestCategoryCode,
  type FaultCategoryRow,
} from '@hk/core'
import type { AppLocale } from '@hk/ui'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-75 · WHAT CAN BE WRONG WITH A ROOM.  Sprint 63.

   ── THIS IS A CORRECTION, NOT A FEATURE (#166) ───────────────────────────────────────
   The list of fault categories lived in two client dictionaries — this application's and
   the handset's — with nothing on the server that had ever heard of a category. The two
   said DIFFERENT WORDS for the same code: `PLUMBING` was "Plumbing" here and "Water or
   drain" on the phone, `DOOR_LOCK` was "Door lock" and "Door or lock". An attendant and
   the supervisor reading his report were looking at different names for one thing, and no
   hotel could change either without a release.

   ── IT IS SPRINT 56'S SCREEN WITH TWO COLUMNS MISSING, ON PURPOSE ────────────────────
   An administrator who has learned the request kinds card has learned this one: same five
   acts, same refusals, same sentences, the code drawn as text with no box to change it in.
   What is gone is the PROMISE and the stock item — a fault is triaged rather than
   promised, and reporting one takes nothing off a shelf.
   ========================================================================= */

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole)
}

function pluralOf(forms: Record<string, string>, count: number, locale: AppLocale): string {
  return forms[new Intl.PluralRules(locale).select(count)] ?? forms.other ?? ''
}

interface Draft { name: string; nameAr: string }

const draftOf = (row: FaultCategoryRow): Draft => ({
  name: row.name,
  nameAr: row.nameAr ?? '',
})

const changed = (row: FaultCategoryRow, draft: Draft) =>
  draft.name !== row.name || draft.nameAr !== (row.nameAr ?? '')

export function FaultCategories({
  propertyId, locale, onError,
}: {
  propertyId: string
  locale: AppLocale
  /** Raised to the screen, which owns the one alert band. */
  onError: (caught: unknown) => void
}) {
  const t = STRINGS[locale]

  const [rows, setRows] = useState<FaultCategoryRow[] | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [adding, setAdding] = useState<{
    code: string
    /** Until the code box has been typed in, the name drives it. */
    codeTouched: boolean
    name: string
    nameAr: string
  } | null>(null)

  const take = useCallback((next: FaultCategoryRow[]) => {
    setRows(next)
    setDrafts(Object.fromEntries(next.map((r) => [r.categoryId, draftOf(r)])))
  }, [])

  const load = useCallback(async () => {
    try {
      take(await fetchFaultCategoryAdmin(propertyId))
    } catch (caught: unknown) {
      onError(caught)
    }
  }, [propertyId, take, onError])

  useEffect(() => { void load() }, [load])

  if (rows === null) return <LoadingState rows={4} />

  async function commit(what: string, run: () => Promise<FaultCategoryRow[]>) {
    setBusy(what)
    try {
      take(await run())
      return true
    } catch (caught: unknown) {
      onError(caught)
      return false
    } finally {
      setBusy(null)
    }
  }

  const codeToUse = adding === null
    ? ''
    : adding.codeTouched ? adding.code.trim().toUpperCase() : suggestCategoryCode(adding.name)

  const addable =
    adding !== null && adding.name.trim().length > 0 && codeWellFormed(codeToUse)

  /* **HOW MANY ARE STILL ON**, so the last one's button can be disabled rather than
     refused. The server checks it too and the server decides; this only stops the screen
     offering a control that cannot work. */
  const stillActive = rows.filter((r) => r.isActive).length

  return (
    <Card>
      <CardHeader title={t.fcTitle} />
      <p className="max-w-[70ch] px-3.5 pt-0 text-[12.5px] leading-relaxed text-muted-foreground">
        {t.fcIntro}
      </p>

      <div className="overflow-x-auto p-3.5">
        {/* The names get room and the rest get what is left — #157's lesson, and the
            minimum width is the SUM of the columns rather than a round number. */}
        <table className="w-full min-w-[46rem] table-fixed text-[12.5px]">
          <colgroup>
            <col className="w-[8rem]" />
            <col className="w-[13rem]" />
            <col className="w-[13rem]" />
            <col className="w-[6rem]" />
            <col className="w-[6rem]" />
          </colgroup>
          <thead>
            <tr className="border-b border-border text-muted-foreground">
              <th className="p-2 text-start font-medium">{t.rcColCode}</th>
              <th className="p-2 text-start font-medium">{t.rcColName}</th>
              <th className="p-2 text-start font-medium">{t.rcColNameAr}</th>
              <th className="p-2 text-start font-medium">{t.fcColRaised}</th>
              <th className="p-2 text-start font-medium sr-only">{t.cfgSave}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const draft = drafts[row.categoryId] ?? draftOf(row)

              return (
                <tr
                  key={row.categoryId}
                  className={`border-b border-border/60 last:border-0 ${
                    row.isActive ? '' : 'opacity-60'}`}
                >
                  {/* **THE CODE IS TEXT, NOT A BOX.** There is no control here to disable,
                      because a disabled control is a question somebody has to ask. */}
                  <td className="p-2 font-mono text-[11.5px] text-muted-foreground">
                    <div>{row.code}</div>
                    {row.isActive ? null : (
                      <div className="mt-0.5 font-sans text-[11px] text-muted-foreground">
                        <Bidi>{t.rcRetired}</Bidi>
                      </div>
                    )}
                  </td>

                  <td className="p-2">
                    <FieldInput
                      aria-label={`${t.rcColName} — ${row.code}`}
                      dir="auto"
                      maxLength={80}
                      value={draft.name}
                      onChange={(e) => setDrafts((d) => ({
                        ...d, [row.categoryId]: { ...draft, name: e.target.value },
                      }))}
                    />
                  </td>

                  <td className="p-2">
                    <FieldInput
                      aria-label={`${t.rcColNameAr} — ${row.code}`}
                      dir="rtl"
                      lang="ar"
                      maxLength={80}
                      value={draft.nameAr}
                      onChange={(e) => setDrafts((d) => ({
                        ...d, [row.categoryId]: { ...draft, nameAr: e.target.value },
                      }))}
                    />
                  </td>

                  <td className="p-2 text-muted-foreground">
                    <Bidi>
                      {row.faultsEverRaised === 0
                        ? t.fcNeverRaised
                        : fill(pluralOf(t.fcRaised, row.faultsEverRaised, locale),
                            { count: formatCount(row.faultsEverRaised, locale) })}
                    </Bidi>
                    {/* **RETIRING DOES NOT CLOSE WHAT IS OPEN**, and the row says so rather
                        than leaving somebody to wonder where they went. */}
                    {!row.isActive && row.openFaults > 0 ? (
                      <div className="mt-0.5 text-[11px]">
                        <Bidi>
                          {fill(pluralOf(t.fcStillOpen, row.openFaults, locale),
                            { count: formatCount(row.openFaults, locale) })}
                        </Bidi>
                      </div>
                    ) : null}
                  </td>

                  <td className="p-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!changed(row, draft) || busy === row.categoryId}
                        aria-busy={busy === row.categoryId}
                        aria-label={`${t.cfgSave} — ${row.code}`}
                        onClick={() => void commit(row.categoryId, () =>
                          saveFaultCategory(propertyId, row.categoryId, {
                            name: draft.name,
                            nameAr: draft.nameAr,
                          }))}
                      >
                        {busy === row.categoryId ? t.cfgSaving : t.cfgSave}
                      </Button>

                      {/* **THE LAST ACTIVE ONE'S BUTTON IS DISABLED, NOT REFUSED.** A hotel
                          with none cannot report a fault at all — the picker on the handset
                          and on the board is empty and nothing says why. */}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy === row.categoryId
                          || (row.isActive && stillActive <= 1)}
                        aria-label={
                          `${row.isActive ? t.rcRetire : t.rcRestore} — ${row.code}`}
                        onClick={() => void commit(row.categoryId, () =>
                          setFaultCategoryActive(propertyId, row.categoryId, !row.isActive))}
                      >
                        {row.isActive ? t.rcRetire : t.rcRestore}
                      </Button>

                      {/* Drawn only where it can work. */}
                      {row.faultsEverRaised === 0 ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={busy === row.categoryId}
                          aria-label={`${t.rcDelete} — ${row.code}`}
                          onClick={() => void commit(row.categoryId, () =>
                            deleteFaultCategory(propertyId, row.categoryId))}
                        >
                          {t.rcDelete}
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* Said once, not once per row — #159. */}
        <p className="mt-3 max-w-[70ch] text-[11.5px] leading-relaxed text-muted-foreground">
          {t.fcKeepHint}
        </p>
      </div>

      {/* ── ADDING ONE ───────────────────────────────────────────────────── */}
      <div className="border-t border-border p-3.5">
        {adding === null ? (
          <Button
            type="button"
            size="sm"
            onClick={() => setAdding({ code: '', codeTouched: false, name: '', nameAr: '' })}
          >
            {t.fcAdd}
          </Button>
        ) : (
          <div className="flex flex-col gap-3">
            <div>
              <h3 className="text-[13px] font-medium">{t.fcAddTitle}</h3>
              <p className="mt-1 max-w-[70ch] text-[12px] leading-relaxed text-muted-foreground">
                {t.fcAddIntro}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t.rcColName}>
                {({ id, 'aria-label': label }) => (
                  <FieldInput
                    id={id} aria-label={label} dir="auto" maxLength={80}
                    value={adding.name}
                    onChange={(e) => setAdding((a) =>
                      a === null ? a : { ...a, name: e.target.value })}
                  />
                )}
              </Field>

              <Field label={t.rcColNameAr} hint={t.cfgNameArHint}>
                {({ id, 'aria-label': label }) => (
                  <FieldInput
                    id={id} aria-label={label} dir="rtl" lang="ar" maxLength={80}
                    value={adding.nameAr}
                    onChange={(e) => setAdding((a) =>
                      a === null ? a : { ...a, nameAr: e.target.value })}
                  />
                )}
              </Field>

              {/* **THE SUGGESTION IS SHOWN, NEVER APPLIED SILENTLY.** The code is the one
                  field that is permanent, which makes it the worst place to be helpful
                  behind somebody's back. */}
              <Field
                label={t.rcCode}
                hint={adding.codeTouched ? t.rcCodeHint : t.rcCodeSuggested}
              >
                {({ id, 'aria-label': label }) => (
                  <FieldInput
                    id={id}
                    aria-label={label}
                    dir="ltr"
                    lang="en"
                    maxLength={32}
                    className="font-mono"
                    value={codeToUse}
                    onChange={(e) => setAdding((a) => a === null ? a : {
                      ...a, codeTouched: true, code: e.target.value.toUpperCase(),
                    })}
                  />
                )}
              </Field>
            </div>

            {adding.codeTouched && codeToUse.length > 0 && !codeWellFormed(codeToUse) ? (
              <p role="alert" className="text-[12px] text-[#b91c1c] dark:text-[#fca5a5]">
                {t.rcCodeBad}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!addable || busy === 'add'}
                aria-busy={busy === 'add'}
                onClick={() => void (async () => {
                  const ok = await commit('add', () => addFaultCategory(propertyId, {
                    code: codeToUse,
                    name: adding.name,
                    nameAr: adding.nameAr,
                  }))
                  /* The form stays open when the server refused, holding what was typed. */
                  if (ok) setAdding(null)
                })()}
              >
                {busy === 'add' ? t.cfgSaving : t.fcAddSave}
              </Button>

              <Button type="button" size="sm" variant="outline" onClick={() => setAdding(null)}>
                {t.rcCancel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
